import { GrowthBook } from '@growthbook/growthbook';
import { growthbookApi } from '@/utils/axiosConfig';
import { isDebug } from '@/utils/debug';
import { currentCountry } from '@/plugins/country/module';
import events from '@/utils/helpers/events';
import store from '@/store/store';
import { getCookie, setCookie } from '@/utils/cookies';
import { log } from '../../utils/logger';
import { isDevMenuEnabled } from '@/utils/devMenu';
import { applyFeatureOverrides } from '@/utils/featureOverrides';

let growthbook = null;
let growthbookFullyInitialized = false;

function getExperimentsEventPayload() {
  const payload = [];
  try {
    const results = growthbook.getAllResults();
    for (const experiment of results) {
      const name = experiment[0];
      const { result } = experiment[1];
      if (result.inExperiment) {
        payload.push(`${name} - ${result.name}`);
      }
    }
  } catch (e) {
    console.log(e.message);
  }
  return payload;
}

function getUserId() {
  const user = store.getters['app/user'];
  let userId = undefined;

  if (user && user.userId) {
    userId = user.userId;
  }

  return userId;
}

function getInTrialLocation() {
  if (currentCountry.toUpperCase() !== 'SE') {
    return false;
  }

  const primaryProposition = store.getters['myPropositions/primaryProposition'];
  if (!primaryProposition || !primaryProposition.residences) {
    return false;
  }

  const { inTrialArea } = primaryProposition;
  return inTrialArea === true;
}

function getUserCreated() {
  const user = store.getters['app/user'];
  let userCreated = undefined;

  if (user && user.registeredAt) {
    userCreated = new Date(user.registeredAt).getTime();
  }

  return userCreated;
}

function getPrimaryPropositionSubscriptionType() {
  return store.getters['myPropositions/primaryPropositionSubscriptionType'];
}

export async function setGrowthbookAttributes(
  extraAttributesObj = {},
  storeInCookie = false
) {
  if (!growthbook) {
    window.Logger.warn(
      'GrowthBook failed to initialize. Feature flags and experiments not active.'
    );
    return false;
  }

  try {
    const deviceId = getCookie('lb-sid');

    const attributes = {
      ...growthbook.getAttributes(),
      country_and_user_id: `${currentCountry.toUpperCase()}_${getUserId()}`,
      subscription_type: getPrimaryPropositionSubscriptionType(),
      user_id: getUserId(),
      user_created: getUserCreated(),
      country: currentCountry.toUpperCase(),
      in_trial_location: getInTrialLocation(),
      deviceId: deviceId || null,
      // Stamped by middlewares/lbSessionMiddleware.js on the visitor's first
      // ever request; absent (and cleaned below) for returning visitors, so
      // new-visitor experiments can target on presence.
      first_seen: Number(getCookie('lb-first-seen')) || undefined,
      landed_on_startpage: getCookie('lb-landed-home') ? true : undefined,
      ...extraAttributesObj
    };

    // GrowthBook treats a key explicitly set to undefined as existing, which
    // fails "$exists: false" targeting (e.g. anonymous-only experiments keyed
    // on user_id) — drop those keys so absent really means absent.
    for (const key of Object.keys(attributes)) {
      if (attributes[key] === undefined) {
        delete attributes[key];
      }
    }

    await growthbook.setAttributes(attributes);
  } catch (error) {
    log('GrowthBook could not set attributes');
  }

  try {
    if (storeInCookie) {
      const enabledFeatures = [];
      for (const key of Object.keys(growthbook.getFeatures())) {
        if (growthbook.isOn(key)) {
          enabledFeatures.push(key);
        }
      }
      const cookieValue = JSON.stringify(enabledFeatures);
      const urlParts = window.location.hostname.split('.');
      const domain = `${urlParts[1]}.${urlParts[2]}`;

      //const currentExperiments = getCookie('lb-experiments');
      //if (currentExperiments !== cookieValue) {
      setCookie('lb-experiments', cookieValue, 1, domain);

      const experimentsPayload = getExperimentsEventPayload();

      events.emitEvent(
        events.eventTypes.EXPERIMENTS,
        'Updated',
        experimentsPayload
      );
      //}
    }
  } catch {
    log('GrowthBook Could not set experiments cookie');
  }
}

export async function initGrowthbook() {
  if (growthbook) {
    return growthbook;
  }

  try {
    const { data } = await growthbookApi.get();

    growthbook = new GrowthBook({
      // TODO: Switch to fetch api when we feel comfortable not using axios
      // apiHost: getGrowthBookEndpoint()
      enableDevMode: isDebug(),
      trackingCallback: (experiment, result) => {
        events.emitEvent(events.eventTypes.EXPERIMENT, experiment.key, {
          experiment,
          result
        });
      },
      attributes: {
        country: currentCountry.toUpperCase()
      }
    });
    growthbook.setFeatures(data.features);
    // Flags forced on or off in the developer menu (testers only).
    applyFeatureOverrides(growthbook, isDevMenuEnabled());

    window.Logger.info('Growthbook plugin initialized');
  } catch (error) {
    window.Logger.error('GrowthBook Vue plugin initialization error', error);
  }

  await setGrowthbookAttributes();

  // Mark fully ready only after user attributes (user_created, user_id, …)
  // are on the SDK — otherwise consumers of `waitForGrowthbookInit` would
  // race against the attribute set and evaluate features with only `country`.
  growthbookFullyInitialized = true;
}

export function isFeatureFlagEnabled(featureFlag) {
  if (!growthbook) {
    window.Logger.warn(
      'GrowthBook failed to initialize. Feature flags and experiments not active.'
    );
    return false;
  }

  //window.Logger.log(`Feature ${featureFlag}:`, growthbook.isOn(featureFlag));
  return growthbook.isOn(featureFlag);
}

export function getFeatureValue(featureFlag, fallbackValue) {
  if (!growthbook) {
    window.Logger.warn(
      'GrowthBook failed to initialize. Feature flags and experiments not active.'
    );
    return fallbackValue;
  }

  // window.Logger.debug(
  //   `Feature ${featureFlag}:`,
  //   growthbook.getFeatureValue(featureFlag, fallbackValue)
  // );

  return growthbook.getFeatureValue(featureFlag, fallbackValue);
}

export function evalFeature(featureFlag) {
  if (!growthbook) {
    window.Logger.warn(
      'GrowthBook failed to initialize. Feature flags and experiments not active.'
    );
    return null;
  }

  return growthbook.evalFeature(featureFlag);
}

// All flags GrowthBook knows, for the developer menu.
export function getFeatureKeys() {
  if (!growthbook) return [];
  return Object.keys(growthbook.getFeatures()).sort();
}

export function waitForGrowthbookInit() {
  return new Promise(resolve => {
    if (growthbookFullyInitialized) {
      resolve();
      return;
    }

    let waitIval = setInterval(() => {
      if (!growthbookFullyInitialized) {
        return;
      }

      clearInterval(waitIval);
      resolve();
    }, 50);
  });
}

export default {
  getFeatureValue,
  isFeatureFlagEnabled,
  evalFeature,
  initGrowthbook,
  setGrowthbookAttributes,
  waitForGrowthbookInit
};
